/**
 * Query history for the DB Manager.
 *
 * What every comparable client has and this one did not: the statement you ran
 * twenty minutes ago, before you edited it into something that no longer works.
 *
 * Kept in localStorage rather than in the workspace snapshot. A snapshot is
 * synced, and history is a local record of what this machine ran — including,
 * unavoidably, literal values people typed into WHERE clauses. It stays here.
 *
 * Entries are capped and statements truncated, because localStorage is a
 * shared, synchronous, size-limited store: a session that pastes a 5 MB dump
 * into the editor must not be able to fill it, or every other feature that
 * writes there starts throwing.
 */

const STORAGE_KEY = 'apiboo.db.queryHistory.v1';

export const MAX_HISTORY_ENTRIES = 300;
// Long enough to hold any statement a person typed; short enough that 300 of
// them cannot approach a localStorage quota.
export const MAX_SQL_CHARS = 8000;

let entries = null; // lazy, so importing this module costs nothing

function read() {
  if (entries) return entries;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    entries = Array.isArray(parsed) ? parsed.filter((e) => e && typeof e.sql === 'string') : [];
  } catch {
    // Corrupt or unavailable storage (private mode, quota, a half-written
    // value) must not take the DB Manager down with it — history is the least
    // important thing on the page.
    entries = [];
  }
  return entries;
}

function write() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Over quota: drop the oldest half and try once. If that fails too, the
    // in-memory list still works for this session.
    //
    // Never below one entry. Entries are newest-first, so a plain halving of a
    // one-element list keeps zero of them — and the one it would throw away is
    // the statement that just ran, which is the whole reason anyone opens this.
    try {
      entries = entries.slice(0, Math.max(1, Math.floor(entries.length / 2)));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch {
      /* give up quietly */
    }
  }
}

/** Newest first. */
export function listQueryHistory({ connId = null, search = '' } = {}) {
  const all = read();
  const term = String(search || '').trim().toLowerCase();
  return all.filter((e) => {
    if (connId && e.connId !== connId) return false;
    if (term && !e.sql.toLowerCase().includes(term)) return false;
    return true;
  });
}

/**
 * Record an execution.
 *
 * Re-running the identical statement on the same connection updates the
 * existing entry in place instead of appending a duplicate — otherwise
 * iterating on one query buries everything else under fifty copies of it.
 */
export function recordQueryHistory({
  sql,
  connId,
  engine,
  dbName,
  durationMs,
  rowCount,
  error,
  at,
}) {
  const text = String(sql || '').trim();
  if (!text) return;

  const list = read();
  const entry = {
    sql: text.length > MAX_SQL_CHARS ? `${text.slice(0, MAX_SQL_CHARS)}\n-- […truncated]` : text,
    connId: connId || null,
    engine: engine || null,
    dbName: dbName || null,
    durationMs: Number.isFinite(durationMs) ? Math.round(durationMs) : null,
    rowCount: Number.isFinite(rowCount) ? rowCount : null,
    error: error ? String(error).slice(0, 500) : null,
    at: Number.isFinite(at) ? at : Date.now(),
    runs: 1,
  };

  const dupeIndex = list.findIndex((e) => e.sql === entry.sql && e.connId === entry.connId);
  if (dupeIndex !== -1) {
    entry.runs = (list[dupeIndex].runs || 1) + 1;
    list.splice(dupeIndex, 1);
  }

  list.unshift(entry);
  if (list.length > MAX_HISTORY_ENTRIES) list.length = MAX_HISTORY_ENTRIES;
  entries = list;
  write();
}

export function clearQueryHistory() {
  entries = [];
  write();
}

/** Drop one entry — for the "this had a password in it" case. */
export function removeQueryHistoryAt(index) {
  const list = read();
  if (index < 0 || index >= list.length) return;
  list.splice(index, 1);
  entries = list;
  write();
}

/** Test seam: forget what was read so the next call re-reads storage. */
export function resetQueryHistoryCache() {
  entries = null;
}
